import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Link,
  Container,
  Button,
  Grid,
  Divider
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthProfile } from '../../app/hooks';

import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  IconButton,
} from '@mui/material';


//const imageUrl = "/map/preview/ny2026/image.jpg";

const EventPreview: React.FC<{ imageUrl?: string }> = ({ imageUrl = "/map/preview/ny2026/image.jpg" }) => {
  const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile } = useAuthProfile()
  const [params, setSearchParams] = useSearchParams();
  const t = params.get("t");
  const userSignIn = params.get("userSignIn") === 'true'
  const noLoginPrompt = params.get("nlp") === 'true'
  const phooneNumberToLink = params.get("phone");
  const conversationId = params.get("conversationId");
  const [openImage, setOpenImage] = useState(false);

  const navigate = useNavigate()

  function goToResponses() {
    const singinParams = new URLSearchParams({ ...t ? { t } : {}, redirect:'/conversations/?conversationId=1' });
    navigate(`/Conversations?${singinParams.toString()}`)
    //navigate(`/Conversations`)
  }
  return (
    <Box
      id='resolutions-root-box'
      sx={{
        width: '100%',
        // Changed height to minHeight to prevent content clipping
        minHeight: { xs: '100%', md: '100%' },
        // Changed md: 'hidden' to 'auto' to enable laptop/desktop scrolling
        overflow: { xs: 'auto', md: 'auto' },
        bgcolor: '#fafafa'
      }}
    >
      <Stack>
        {/* Header Section */}
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 700, px: 2, pt: 3, mb: 0.5 }}>
          The Kindness We Shared
        </Typography>
        <Typography variant="h8" sx={{ textAlign: 'center', px: 5, mb: { md: 1, lg: 2 }, fontWeight: '700', color: '#919191ff' }}>
          Thank you to those who participated in our February event.
        </Typography>
      </Stack>
      <Divider orientation="horizontal" flexItem />

      {/* Main Content Area */}
      <Container>
        <Box
          sx={{
            width: '100%',
            flexGrow: 1,
            p: { xs: 2, md: 0, xl: 0 },
            // Ensure the inner box also allows scrolling if parent height is constrained
            overflowY: 'auto'
          }}
        >
          <Stack
            gap={2}
            direction="column"
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}

          >
            <Box
              component="img"
              src={imageUrl}
              alt="Acts of kindness"
              onClick={() => setOpenImage(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setOpenImage(true);
              }}
              sx={{
                width: { xs: '100%', sm: '50%' },
                display: 'block',
                objectFit: 'contain',
                mt: { xs: 0, md: 2 },
                ml: { xs: 0, md: 2 },
                cursor: 'zoom-in',
                borderRadius: 2,
                '&:hover': { opacity: 0.95 }
              }}
            />

            <Dialog
              open={openImage}
              onClose={() => setOpenImage(false)}
              fullScreen
              PaperProps={{
                sx: {
                  bgcolor: 'rgba(0,0,0,0.92)',
                  m: 0,
                },
              }}
            >
              <IconButton
                onClick={() => setOpenImage(false)}
                aria-label="Close"
                sx={{
                  position: 'fixed',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  color: 'white',
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 1, md: 3 },
                  overflow: 'auto', // allows panning if image is large
                }}
                onClick={() => setOpenImage(false)} // click backdrop closes
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Acts of kindness full screen"
                  onClick={(e) => e.stopPropagation()} // don't close when clicking image
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    cursor: 'zoom-out',
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Dialog>

            {/* Poem Column - 2 Columns */}

            <Typography variant="h7" align='center' sx={{ fontWeight: 700, mb: { md: 1, xl: 0 } }}>
              Nearly 200 Warriors participated in our Kindness event and shared stories of kindness they received and have given.
            </Typography>

            <Button onClick={goToResponses}>Click to Check out their responses here</Button>

            <Box
              sx={{
                width: { xs: '90%', sm: '50%' },
                columnCount: { xs: 1, sm: 1 },
                columnGap: 4,
                whiteSpace: 'pre-line',
                '& .MuiTypography-root': {
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  lineHeight: 1.5,
                  mb: 2,
                  breakInside: 'avoid-column'
                }
              }}
            >
              <Typography variant="body1" sx={{ mt: 2, fontWeight: 700 }}>
                The Smallest Gestures Made the Biggest Impact. For many, the smallest gestures carried them through their hardest days.  Kindness doesn’t need scale. It needs presence.
              </Typography>
              <Typography variant="body1">
                A hug {"\n"}
                A smile from a stranger{"\n"}
                Someone holding a door{"\n"}
                A “thinking of you” text{"\n"}
                A phone call{"\n"}
                A free coffee or milkshake{"\n"}
                A handwritten card{"\n"}
              </Typography>


              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                The Power of Family.   Love shows up in daily action.
              </Typography>
              <Typography variant="body1">
                Husbands cooking breakfast and dinner
                Daughters preparing meals after hospital stays{"\n"}
                Grandsons saying “I love you” every day{"\n"}
                Siblings quietly stepping in with help{"\n"}
                Family checking in after treatment{"\n"}
                Someone filling up the gas tank{"\n"}
                Someone doing laundry{"\n"}
                A listening ear without judgment{"\n"}
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Faith, Prayer & Encouragement - For some, kindness meant reminding others: “You are not a mistake. You are needed.”
              </Typography>
              <Typography variant="body1">
                Prayer{"\n"}
                Faith in God{"\n"}
                Encouraging messages{"\n"}
                Being reminded they are loved and irreplaceable{"\n"}
                Sharing spiritual support with others{"\n"}
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Giving Kindness is Just as Powerful.  Even while fighting cancer, people still found ways to lift others. Many didn’t just receive kindness — they gave it:
              </Typography>
              <Typography variant="body1">
                Donating blankets{"\n"}
                Supporting trafficking survivors{"\n"}
                Making meals for others{"\n"}
                Letting someone go ahead in line{"\n"}
                Paying for someone’s coffee{"\n"}
                Checking on others{"\n"}
                Volunteering{"\n"}
                Speaking kindly to strangers{"\n"}
                Sharing their patient story to help others
              </Typography>


              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                The Hard Truth - One participant wrote: “Nothing yet. It’s been a very rough week.”
              </Typography>
              <Typography variant="body1">
                That matters too.{"\n"}
                Kindness isn’t constant.{"\n"}
                Some weeks are heavy.{"\n"}
                Some days are empty.{"\n"}
                And that’s exactly why this community exists.
              </Typography>


              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                A Message Back to You
              </Typography>
              <Typography variant="body1">
                Kindness is relational.{"\n"}
                Kindness is often small.{"\n"}
                Kindness is practical.{"\n"}
                Kindness often comes through family.{"\n"}
                Kindness travels both directions.{"\n"}
                And sometimes… kindness is simply being heard.
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                What We Learned About Kindness
              </Typography>
              <Typography variant="body1">
                Reading these responses makes something clear:
                You are not just patients.{"\n"}
                You are caregivers.{"\n"}
                You are encouragers.{"\n"}
                You are neighbors.{"\n"}
                You are believers.{"\n"}
                You are fighters.{"\n"}
                You are givers.{"\n"}
                And even in treatment, even in fatigue, even in fear — you are still choosing kindness.

              </Typography>
            </Box>


            <Typography align="center" variant="h6" sx={{ fontWeight: 700, mb: { md: 1, xl: 0 } }}>
              Thank you Warriors for participating this amazing event! See you at the March event!
            </Typography>


          </Stack>

          {/* Footer Button */}
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            sx={{ mt: 4, pb: 3 }}
          >
            <Typography variant="body1" sx={{ mt: { sm: 3, md: 1, xl: 4 }, fontWeight: 600, textAlign: 'center' }}>
              {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) ? ' Ready to join the community?' : 'Patient Connect'}
            </Typography>
            {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) &&
              <Button id="event2026-singup"
                variant="contained"
                disableElevation
                sx={{
                  textTransform: 'none',
                  borderRadius: 10,
                  px: 6,
                  py: 1,
                  fontSize: '1rem',
                  backgroundColor: "#5468FF",
                  '&:hover': { backgroundColor: '#4353CC' }
                }}
                onClick={() => {
                  const singinParams = new URLSearchParams({ ...t ? { t } : {} });
                  navigate(`/SignInToMap?${singinParams.toString()}`)
                }}
              >
                Sign Up
              </Button>}
            <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: { md: 1, lg: 2 }, fontWeight: { sm: '400', md: '700' }, color: '#919191ff' }}>
              Whether you are newly diagnosed, in remission, preparing for treatment or supporting someone you love, Patient Connect is here to help you navigate your journey - together.
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default EventPreview;